import type { ReactNode } from "react";
import { ActivityItem, DecisionStatus } from "shared/app/ActivityLog/types";
import { AGENT_ACTIVITY_TYPES } from "shared/app/ActivityLog/constants";
import { ApplicationStatus } from "shared/types";
import { cx } from "shared/utils";
import {
  Check,
  Mail01,
  ThumbsUp,
  Send01,
  CursorClick02,
  Archive,
  XClose,
  CornerUpLeft
} from "untitledui-js-base";
import {
  CashbackIcon,
  MessageOutgoing01Icon,
  SecurityBlockIcon
} from "@hugeicons-pro/core-stroke-rounded";
import {
  MailEdit01Icon,
  Mail02Icon,
  MailRemove02Icon,
  Wallet04Icon,
  UserSettings01Icon,
  DashboardSpeed01Icon,
  Shield01Icon,
  LogoutCircle01Icon,
  CreditCardIcon,
  Pen01Icon,
  PauseIcon,
  PlayIcon,
  DiscountIcon,
  PathfinderDivideIcon,
  File02Icon,
  Mail01Icon,
  PathfinderUniteIcon,
  SentIcon,
  UserCircle02Icon,
  UserStar01Icon,
  IdentityCardIcon,
  PassportExpiredIcon,
  PassportValidIcon,
  FaceIdIcon,
  PencilEdit02Icon,
  FileEditIcon,
  Home05Icon,
  CircleArrowRight02Icon,
  CancelCircleIcon
} from "@hugeicons-pro/core-solid-rounded";
import { HugeiconsIcon } from "@hugeicons/react";

import CanadaFlag from "shared/icons/canada_flag_icon.svg?react";
import { IconAIApplicationSupportAgent } from "shared/components/ai/IconAI/icons/IconAIApplicationSupportAgent";

type ActivityType = ActivityItem["activity_type"];

const baseBullet =
  "h-10 min-w-10 border rounded-full border-alpha-100 -translate-x-5 flex items-center justify-center";

const WhiteBullet = ({
  children,
  mt = "mt-4"
}: {
  children: ReactNode;
  mt?: string;
}) => <div className={`${mt} bg-white ${baseBullet}`}>{children}</div>;

const PrimaryBullet = ({ children }: { children: ReactNode }) => (
  <div className={`mt-4 bg-primary-500 ${baseBullet}`}>{children}</div>
);

// Uses h-10 w-10 (not min-w-10) and no border — visually distinct from baseBullet.
const SolidPrimaryBullet = ({ children }: { children: ReactNode }) => (
  <div className="shrink-0 mt-4 h-10 w-10 bg-primary-500 rounded-full flex items-center justify-center -translate-x-5">
    {children}
  </div>
);

const ManualReportBullet = ({
  verificationName
}: {
  verificationName: string | null | undefined;
}) => {
  if (verificationName === "Credit report")
    return (
      <WhiteBullet>
        <HugeiconsIcon
          icon={DashboardSpeed01Icon}
          size={24}
          className="text-content-300"
        />
      </WhiteBullet>
    );
  if (verificationName === "Housing court report")
    return (
      <WhiteBullet>
        <HugeiconsIcon
          icon={LogoutCircle01Icon}
          size={24}
          className="text-content-300"
        />
      </WhiteBullet>
    );
  if (verificationName?.includes("criminal"))
    return (
      <WhiteBullet>
        <HugeiconsIcon
          icon={Shield01Icon}
          size={24}
          className="text-content-300"
        />
      </WhiteBullet>
    );
  return null;
};

const getPremiumSurveyBullet = (
  activityType: ActivityType
): ReactNode | undefined => {
  switch (activityType) {
    case "premium_rental_history_survey_sent":
    case "premium_rental_history_survey_resent":
    case "premium_rental_history_survey_manually_sent":
      return (
        <PrimaryBullet>
          <HugeiconsIcon
            icon={
              activityType === "premium_rental_history_survey_manually_sent"
                ? MailEdit01Icon
                : Mail02Icon
            }
            size={24}
            color="#ffffff"
          />
        </PrimaryBullet>
      );

    case "premium_rental_history_survey_landlord_details_updated":
      return (
        <WhiteBullet>
          <HugeiconsIcon
            icon={PencilEdit02Icon}
            size={24}
            className="fill-content-300 text-content-300"
          />
        </WhiteBullet>
      );

    case "premium_rental_history_survey_log_outreach":
      return (
        <WhiteBullet>
          <HugeiconsIcon
            icon={SentIcon}
            size={24}
            className="fill-content-300 text-content-300"
          />
        </WhiteBullet>
      );

    case "premium_rental_history_survey_case_closed":
      return (
        <WhiteBullet>
          <Archive size="24" className="stroke-content-400" />
        </WhiteBullet>
      );

    case "premium_rental_history_survey_declined":
      return (
        <div className={cx("mt-4 bg-content-400", baseBullet)}>
          <HugeiconsIcon icon={MailRemove02Icon} size={24} color="#ffffff" />
        </div>
      );

    default:
      return undefined;
  }
};

const getActivityBullet = (
  activityType: ActivityType,
  verificationName: string | null | undefined
): ReactNode | undefined => {
  // eslint-disable-next-line sonarjs/max-switch-cases -- that's the exact purpose of switch
  switch (activityType) {
    case "verification_override":
    case "income_override":
    case "income_reverify_completed":
    case "income_reverify_canceled":
    case "income_reverify":
    case "income_reverify_request_completed":
    case "income_reverify_request_cancelled":
    case "income_reverify_request_created":
    case "income_reverify_request_failed":
      return (
        <SolidPrimaryBullet>
          <HugeiconsIcon className="text-white" size={24} icon={Wallet04Icon} />
        </SolidPrimaryBullet>
      );

    case "request_retry_identity_verification":
      return (
        <WhiteBullet>
          <HugeiconsIcon icon={IdentityCardIcon} className="text-content-300" />
        </WhiteBullet>
      );

    case "cancel_request_retry_identity_verification":
      return (
        <WhiteBullet>
          <HugeiconsIcon
            icon={PassportExpiredIcon}
            className="text-content-300"
          />
        </WhiteBullet>
      );

    case "complete_request_retry_identity_verification":
      return (
        <WhiteBullet>
          <HugeiconsIcon
            icon={PassportValidIcon}
            className="text-content-300"
          />
        </WhiteBullet>
      );

    case "idv_data_updated":
      return (
        <WhiteBullet mt="mt-2">
          <HugeiconsIcon
            icon={FaceIdIcon}
            className="text-content-200"
            size={24}
          />
        </WhiteBullet>
      );

    case "failed_identity_verification_manual_override":
      return (
        <div className={`mt-4 bg-danger-500 ${baseBullet}`}>
          <HugeiconsIcon icon={FaceIdIcon} size={24} className="text-white" />
        </div>
      );
    case "applicant_uploaded_additional_document":
    case "partner_requested_additional_document":
      return (
        <WhiteBullet>
          <HugeiconsIcon
            icon={SentIcon}
            size={24}
            className="text-content-300"
          />
        </WhiteBullet>
      );

    case "partner_canceled_additional_document_request":
      return (
        <WhiteBullet>
          <HugeiconsIcon
            icon={CancelCircleIcon}
            size={24}
            className="text-content-400"
          />
        </WhiteBullet>
      );

    case "role_changed":
      return (
        <WhiteBullet>
          <HugeiconsIcon
            icon={UserSettings01Icon}
            size={24}
            className="text-content-300"
          />
        </WhiteBullet>
      );

    case "name_changed":
      return (
        <WhiteBullet>
          <HugeiconsIcon
            icon={Pen01Icon}
            size={24}
            className="text-content-300"
          />
        </WhiteBullet>
      );

    case "income_finished_by_override":
    case "verified_income_adjusted":
      return (
        <WhiteBullet>
          <HugeiconsIcon
            icon={CreditCardIcon}
            size={24}
            className="text-content-300"
          />
        </WhiteBullet>
      );

    case "canadian_criminal_report_invited":
      return (
        <WhiteBullet>
          <CanadaFlag />
        </WhiteBullet>
      );

    case "group_submitted":
      return (
        <PrimaryBullet>
          <CursorClick02 size="24" className="stroke-white" />
        </PrimaryBullet>
      );

    case "invited":
      return (
        <WhiteBullet>
          <Send01 size="24" className="stroke-content-400" />
        </WhiteBullet>
      );

    case "resent_invite":
      return (
        <WhiteBullet mt="mt-8">
          <Send01 size="24" className="stroke-content-400" />
        </WhiteBullet>
      );

    case "decision_email_sent":
    case "undo_decision_email_sent":
    case "credit_report_sent":
    case "compliance_report_sent":
    case "workflow_email_sent":
      return (
        <WhiteBullet>
          <Mail01 size="24" className="stroke-content-400" />
        </WhiteBullet>
      );

    case "undo_decision":
      return (
        <PrimaryBullet>
          <CornerUpLeft size="24" className="stroke-white" />
        </PrimaryBullet>
      );

    case "workflow_sms_sent":
      return (
        <WhiteBullet>
          <HugeiconsIcon
            icon={MessageOutgoing01Icon}
            size={24}
            className="text-content-400"
          />
        </WhiteBullet>
      );

    case "itemized_receipt_sent":
      return (
        <WhiteBullet>
          <HugeiconsIcon
            icon={SentIcon}
            size={24}
            className="fill-content-300 text-content-300"
          />
        </WhiteBullet>
      );

    case "housing_history_added":
      return (
        <WhiteBullet>
          <HugeiconsIcon
            icon={Home05Icon}
            size={24}
            className="fill-content-300 text-content-300"
          />
        </WhiteBullet>
      );

    case "report_run_manually":
      return <ManualReportBullet verificationName={verificationName} />;

    case "submitted":
      return (
        <div className={`mt-4 bg-primary-500 ${baseBullet}`}>
          <div className="w-5 h-5 bg-white rounded-full flex items-center justify-center">
            <Check size="14" className="stroke-primary-500" />
          </div>
        </div>
      );

    case "requested_pet_screening":
      return (
        <WhiteBullet>
          <div className="w-5 h-5 bg-white rounded-full flex items-center justify-center">
            <HugeiconsIcon
              icon={Mail01Icon}
              className="text-content-400"
              size={24}
            />
          </div>
        </WhiteBullet>
      );

    case "auto_approved":
      return (
        <div className={`mt-4 bg-success-600 ${baseBullet}`}>
          <ThumbsUp size="24" className="stroke-success-600" fill="white" />
        </div>
      );

    case "canceled":
    case "uncanceled":
    case "invite_canceled":
      return (
        <WhiteBullet>
          <Archive size="24" className="stroke-content-400" />
        </WhiteBullet>
      );

    case "remove_criminal_report":
      return (
        <WhiteBullet>
          <HugeiconsIcon
            icon={SecurityBlockIcon}
            size={24}
            className="fill-content-300 text-content-300"
          />
        </WhiteBullet>
      );

    case "landlord_survey_first_nudge":
    case "landlord_survey_second_nudge":
    case "landlord_survey_final_nudge":
    case "landlord_survey_sent":
    case "landlord_survey_manual_resent":
      return (
        <PrimaryBullet>
          <HugeiconsIcon
            icon={
              activityType === "landlord_survey_manual_resent"
                ? MailEdit01Icon
                : Mail02Icon
            }
            size={24}
            color="#ffffff"
          />
        </PrimaryBullet>
      );

    case "placed_on_hold":
    case "resumed":
      return (
        <WhiteBullet>
          <HugeiconsIcon
            icon={activityType === "placed_on_hold" ? PauseIcon : PlayIcon}
            size={24}
            className="fill-content-300 text-content-300"
          />
        </WhiteBullet>
      );

    case "discount_applied":
    case "discount_removed":
      // border-primary-500 (not border-alpha-100) — intentional visual difference.
      return (
        <div className="mt-4 bg-primary-500 h-10 min-w-10 border rounded-full border-primary-500 -translate-x-5 flex items-center justify-center">
          <HugeiconsIcon icon={DiscountIcon} size={20} className="text-white" />
        </div>
      );

    case "created_from_split":
    case "split_application":
    case "application_merged":
      return (
        <WhiteBullet>
          <HugeiconsIcon
            icon={
              activityType === "application_merged"
                ? PathfinderUniteIcon
                : PathfinderDivideIcon
            }
            size={24}
            className="fill-content-300 text-content-300"
          />
        </WhiteBullet>
      );

    case "application_rent_adjusted":
    case "application_rent_automatically_adjusted":
    case "applicant_pet_rent_adjusted":
    case "applicant_housing_assistance_adjusted":
      return (
        <WhiteBullet>
          <HugeiconsIcon
            icon={Pen01Icon}
            size={24}
            className="text-content-400"
          />
        </WhiteBullet>
      );

    case "verification_template_overridden":
    case "verification_template_changed":
      return (
        <WhiteBullet>
          <HugeiconsIcon
            icon={File02Icon}
            size={24}
            className="fill-content-200 text-content-200"
          />
        </WhiteBullet>
      );

    case "desired_move_in_date_updated":
    case "desired_lease_term_updated":
      return (
        <WhiteBullet>
          <HugeiconsIcon
            icon={PencilEdit02Icon}
            size={24}
            className="fill-content-300 text-content-300"
          />
        </WhiteBullet>
      );

    case "application_assigned":
    case "primary_applicant_changed":
      return (
        <WhiteBullet>
          <HugeiconsIcon
            icon={
              activityType === "application_assigned"
                ? UserCircle02Icon
                : UserStar01Icon
            }
            size={24}
            className="text-content-300"
          />
        </WhiteBullet>
      );

    case "application_moved_to_new_unit":
      return (
        <WhiteBullet>
          <HugeiconsIcon
            icon={CircleArrowRight02Icon}
            size={24}
            className="text-content-300"
          />
        </WhiteBullet>
      );

    case "fee_refunded":
      return (
        <WhiteBullet>
          <HugeiconsIcon
            icon={CashbackIcon}
            size={24}
            className="text-content-400"
          />
        </WhiteBullet>
      );
    case "income_items_assigned":
      return (
        <PrimaryBullet>
          <HugeiconsIcon icon={Wallet04Icon} size={24} className="text-white" />
        </PrimaryBullet>
      );

    case "manual_application_started":
      return (
        <WhiteBullet>
          <HugeiconsIcon
            icon={FileEditIcon}
            size={24}
            className="fill-content-300 text-content-300"
          />
        </WhiteBullet>
      );

    default:
      return undefined;
  }
};

const getDecisionBullet = (
  decisionStatus: DecisionStatus
): ReactNode | undefined => {
  switch (decisionStatus) {
    case ApplicationStatus.Approved:
      return (
        <div className={`mt-4 bg-success-600 ${baseBullet}`}>
          <ThumbsUp size="24" className="stroke-success-600" fill="white" />
        </div>
      );
    case ApplicationStatus.ConditionalApproved:
      return (
        <div className={`mt-4 bg-warning-500 ${baseBullet}`}>
          <ThumbsUp size="24" className="stroke-warning-500" fill="white" />
        </div>
      );
    case ApplicationStatus.Rejected:
      return (
        <div className={`mt-4 bg-danger-500 ${baseBullet}`}>
          <ThumbsUp
            size="24"
            className="stroke-danger-500 rotate-180"
            fill="white"
          />
        </div>
      );
    default:
      return undefined;
  }
};

const getPmsPushBullet = (activityItem: ActivityItem): ReactNode => {
  const pmsPushStatus =
    activityItem.activity_type ===
    "push_applicants_to_property_management_system"
      ? activityItem.details.pms_push_status
      : undefined;

  if (pmsPushStatus === "success")
    return (
      <div className={`mt-4 bg-success-600 ${baseBullet}`}>
        <Check size="24" className="stroke-white" />
      </div>
    );

  if (pmsPushStatus === "failed")
    return (
      <div className={`mt-4 bg-danger-500 ${baseBullet}`}>
        <XClose size="24" className="stroke-white" />
      </div>
    );

  return (
    <WhiteBullet>
      <XClose size="24" className="stroke-content-400" />
    </WhiteBullet>
  );
};

const getGuestCardBullet = (activityItem: ActivityItem): ReactNode => {
  const guestCardStatus =
    activityItem.activity_type === "appfolio_guest_card_created"
      ? activityItem.details.guest_card_status
      : undefined;

  if (guestCardStatus === "success")
    return (
      <div className={`mt-4 bg-success-600 ${baseBullet}`}>
        <Check size="24" className="stroke-white" />
      </div>
    );

  if (guestCardStatus === "failed")
    return (
      <div className={`mt-4 bg-danger-500 ${baseBullet}`}>
        <XClose size="24" className="stroke-white" />
      </div>
    );

  return (
    <WhiteBullet>
      <XClose size="24" className="stroke-content-400" />
    </WhiteBullet>
  );
};

export const BulletItem = ({
  activityItem,
  decisionStatus
}: {
  decisionStatus?: DecisionStatus;
  activityItem: ActivityItem;
}) => {
  const { activity_type } = activityItem;

  if (activity_type === "decision_made" && decisionStatus)
    return getDecisionBullet(decisionStatus) ?? null;

  if (activity_type === "push_applicants_to_property_management_system")
    return getPmsPushBullet(activityItem);

  if (activity_type === "appfolio_guest_card_created")
    return getGuestCardBullet(activityItem);

  if (activity_type === "appfolio_guest_card_already_exists")
    return (
      <div className={`mt-4 bg-success-600 ${baseBullet}`}>
        <Check size="24" className="stroke-white" />
      </div>
    );

  if (activity_type === "credit_report_rerun_requested")
    return (
      <div className="mt-4 bg-white h-10 min-w-10 border rounded-full border-alpha-100 -translate-x-5 flex items-center justify-center">
        <HugeiconsIcon
          icon={DashboardSpeed01Icon}
          size={24}
          className="text-content-400"
        />
      </div>
    );

  if (
    AGENT_ACTIVITY_TYPES.includes(
      activity_type as (typeof AGENT_ACTIVITY_TYPES)[number]
    )
  )
    return (
      <div className="mt-4 -translate-x-5">
        <IconAIApplicationSupportAgent bgSize={40} />
      </div>
    );

  const verificationName =
    activityItem.activity_type === "report_run_manually"
      ? activityItem.details?.verification_title
      : null;

  return (
    getPremiumSurveyBullet(activity_type) ??
    getActivityBullet(activity_type, verificationName) ?? (
      <div className="h-10 min-w-10" />
    )
  );
};
